import React from 'react';
import { MessageSquarePlus, Trash2 } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { AiChatConversation } from '../types';

interface ConversationSidebarProps {
  conversations: AiChatConversation[];
  selectedId: number | null;
  loading?: boolean;
  onSelect: (id: number) => void;
  onNewChat: () => void;
  onDelete: (id: number) => void;
  creating?: boolean;
  touchFriendly?: boolean;
}

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  selectedId,
  loading,
  onSelect,
  onNewChat,
  onDelete,
  creating,
  touchFriendly = false,
}) => {
  const { colors } = useTheme();

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 w-full max-w-[280px] flex-shrink-0 flex-col border-r',
        colors.border,
        colors.surface,
      )}
    >
      <div className={cn('flex-shrink-0 border-b p-3', colors.border)}>
        <button
          type="button"
          onClick={onNewChat}
          disabled={creating}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
            'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60',
          )}
        >
          <MessageSquarePlus className="h-4 w-4 shrink-0" aria-hidden />
          {creating ? 'Creating…' : 'New chat'}
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {loading ? (
          <p className={cn('px-2 py-3 text-xs', colors.textMuted)}>Loading history…</p>
        ) : conversations.length === 0 ? (
          <p className={cn('px-2 py-3 text-xs leading-relaxed', colors.textMuted)}>
            No conversations yet. Start a new chat to ask the AI assistant anything.
          </p>
        ) : (
          <ul className="space-y-1">
            {conversations.map((c) => {
              const active = selectedId === c.id;
              const preview = c.messages?.[0]?.content;
              return (
                <li key={c.id}>
                  <div
                    className={cn(
                      'group flex items-start gap-1 rounded-lg border transition-colors',
                      active
                        ? 'border-indigo-500/50 bg-indigo-500/10'
                        : cn(colors.border, 'border-transparent hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'),
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(c.id)}
                      className="min-w-0 flex-1 px-3 py-2.5 text-left"
                    >
                      <p className={cn('truncate text-sm font-medium', colors.text)}>{c.title}</p>
                      {preview ? (
                        <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>{preview}</p>
                      ) : null}
                      <p className={cn('mt-1 text-[10px]', colors.textMuted)}>
                        {formatRelativeTime(c.updatedAt)}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(c.id);
                      }}
                      className={cn(
                        'mt-2 mr-1 rounded-md p-1.5 transition-opacity',
                        touchFriendly ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                        colors.textMuted,
                        colors.interactiveHover,
                      )}
                      title="Delete conversation"
                      aria-label="Delete conversation"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};

export default ConversationSidebar;
